"use client";
/*
 * Documentation:
 * DC_Text_Citation — https://app.subframe.com/4f62853ee923/library?component=DC_Text_Citation_d3e77a6d-0e14-44c6-8d63-65f4952d1644
 */

import React from "react";
import { FeatherArrowUpRight } from "@subframe/core";
import * as SubframeUtils from "../utils";

interface ReadingCompanionIndexProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

const ReadingCompanionIndex = React.forwardRef<
  HTMLDivElement,
  ReadingCompanionIndexProps
>(function ReadingCompanionIndex(
  { children, className, ...otherProps }: ReadingCompanionIndexProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex items-center gap-2",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      <span className="text-caption font-caption text-compcolortextcitationtrigger inline pr-2">
        [1]
      </span>
    </div>
  );
});

interface SourceButtonProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

const SourceButton = React.forwardRef<HTMLDivElement, SourceButtonProps>(
  function SourceButton(
    { children, className, ...otherProps }: SourceButtonProps,
    ref
  ) {
    return (
      <div
        className={SubframeUtils.twClassNames(
          "group/ac4c1f4b cursor-pointer items-start inline hover:inline hover:underline",
          className
        )}
        ref={ref}
        {...otherProps}
      >
        {children ? (
          <span className="text-caption font-caption text-brand-700">
            {children}
          </span>
        ) : null}
        <FeatherArrowUpRight className="text-caption font-caption text-brand-700 ml-0.5 align-middle pb-0.5" />
      </div>
    );
  }
);

interface TextProps extends React.HTMLAttributes<HTMLSpanElement> {
  children?: React.ReactNode;
  variant?: "inactive" | "active" | "scrolled-past";
  className?: string;
}

const Text = React.forwardRef<HTMLSpanElement, TextProps>(function Text(
  { children, variant = "inactive", className, ...otherProps }: TextProps,
  ref
) {
  return children ? (
    <span
      className={SubframeUtils.twClassNames(
        "group/aab0a05b w-full cursor-pointer text-caption font-caption text-default-font inline hover:underline",
        {
          "text-subtext-color": variant === "scrolled-past",
          "text-caption-bold font-caption-bold text-compcolortextcitationtrigger hover:text-compcolortextcitationtriggerhover":
            variant === "active",
        },
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {children}
    </span>
  ) : null;
});

interface Dc_Text_CitationRootProps
  extends React.HTMLAttributes<HTMLDivElement> {
  linkSlot?: React.ReactNode;
  text?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const Dc_Text_CitationRoot = React.forwardRef<
  HTMLDivElement,
  Dc_Text_CitationRootProps
>(function Dc_Text_CitationRoot(
  {
    linkSlot,
    text,
    children,
    className,
    ...otherProps
  }: Dc_Text_CitationRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex flex-col items-start gap-2",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {children ? (
        <div className="flex w-full flex-col items-start gap-2">{children}</div>
      ) : null}
      {linkSlot ? (
        <div className="flex w-full items-start gap-4">{linkSlot}</div>
      ) : null}
    </div>
  );
});

export const Dc_Text_Citation = Object.assign(Dc_Text_CitationRoot, {
  ReadingCompanionIndex,
  SourceButton,
  Text,
});
